import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Field, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import CustomInput from '../../../components/common/CustomInput'
import CustomSelect from '../../../components/common/CustomSelect'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { useFestivalContext } from '../../../context/festival'

const validationSchema = Yup.object({
  festName: Yup.string().trim().required('Festival Name is Required'),
  festImages: Yup.array()
    .min(1, 'At least one image is required')
    .required('Images are required'),
  dateOfPost: Yup.date().required('Date is required'),
})

function Edit() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [festivalData, setFestival] = useState(null)
  const [imagePreviews, setImagePreviews] = useState([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [manualFestName, setManualFestName] = useState('')
  const [isManualEntry, setIsManualEntry] = useState(false)
  const [manualDate, setManualDate] = useState([])

  const { festival } = useFestivalContext()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      festName: isManualEntry ? manualFestName : festivalData?.festName || '',
      festImages: festivalData?.festImages || [],
      dateOfPost: isManualEntry ? manualDate : festivalData?.dateOfPost || '',
    },
    validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm()
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers)
      } else {
        formikHelpers.setTouched(error)
      }
    },
  })

  useEffect(() => {
    if (isManualEntry) {
      formik.setFieldValue('festName', manualFestName || '')
      const formattedManualDate = manualDate.split('T')[0]      
      formik.setFieldValue('dateOfPost', formattedManualDate || '')
    } else if (festivalData) {
      formik.setFieldValue('festName', festivalData.festName || '')
    }
  }, [isManualEntry, manualFestName, festivalData])

  const handleFileChange = (event) => {
    const files = event.target.files
    if (files.length === 0) return

    const fileArray = Array.from(files)
    formik.setFieldValue('festImages', fileArray)

    const previews = fileArray.map((file) => URL.createObjectURL(file))
    setImagePreviews(previews)
  }

  const festivalOptions = festival.map((festItem) => ({
    value: festItem.summary,
    label: festItem.summary,
  }))

  useEffect(() => {
    if (!isManualEntry && formik.values.festName) {
      const selectedFestival = festival.find(
        (fest) => fest.summary === formik.values.festName,
      )
      if (selectedFestival) {
        const formattedDate = selectedFestival.start.date.split('T')[0]
        formik.setFieldValue('dateOfPost', formattedDate)
      }
    }
  }, [formik.values.festName, isManualEntry, festival])

  const fetchData = async () => {
    try {
      const response = await axiosAuthInstance.get(`festival/detail/${id}`)
      if (response.data && response.data.result) {
        const festivalData = response.data.result
        setFestival(festivalData)
        setManualDate(festivalData.dateOfPost)
        setImagePreviews(festivalData.festImages)
        setLoading(false)
      }
    } catch (error) {
      console.log('Error fetching edit data:', error)
    }
  }

  const handleSubmit = async (values) => {
    setIsSubmitted(true)
    try {
      const formData = new FormData()
      formData.append(
        'festName',
        isManualEntry ? manualFestName : values.festName,
      )
      formData.append('dateOfPost', values.dateOfPost)
      values.festImages.forEach((file) => {
        formData.append('festImages', file)
      })

      const response = await axiosAuthInstance.put(
        `festival/update/${id}`,
        formData,
      )
      if (response.status === 200) {
        navigate('/festival')
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsSubmitted(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <FormikProvider value={formik}>
      <form className='flex flex-col p-4 gap-4' onSubmit={formik.handleSubmit}>
        <div className='w-full'>
          <h5 className='text-sm sm:text-base font-normal'>
            Festival or Event Images<span className='text-[#D34053]'>*</span>
          </h5>
          <div className='w-full border rounded-lg p-[9px]'>
            <input
              type='file'
              name='festImages'
              accept='image/*'
              multiple
              onChange={handleFileChange}
            />
            {formik.errors.festImages && (
              <div className='text-red-500'>{formik.errors.festImages}</div>
            )}
          </div>
          <div className='flex gap-2 flex-wrap mt-2'>
            {imagePreviews.map((src, index) => (
              <img
                key={index}
                src={src}
                alt='preview'
                className='w-32 h-20 object-cover rounded'
              />
            ))}
          </div>
        </div>

        <div className='w-full'>
        {isManualEntry && (
          <h5 className='text-sm sm:text-base font-normal'>
            Festival or Event Name<span className='text-[#D34053]'>*</span>
          </h5>
          )
            }
          {!isManualEntry ? (
            <Field
              name='festName'
              required
              label='Festival or Event Name'
              component={CustomSelect}
              options={festivalOptions}
              onChange={(option) => formik.setFieldValue('festName', option.value)}
            />
          ) : (
            <input
              type='text'
              name='festName'
              placeholder='Enter Festival or Event Name'
              value={manualFestName}
              onChange={(e) => {
                setManualFestName(e.target.value);
                formik.setFieldValue('festName', e.target.value);
              }}
              className='border rounded-lg p-2 w-full'
            />
          )}
          <button
            type='button'
            className='text-blue-500 mt-2 underline'
            onClick={() => {
              setIsManualEntry((prev) => !prev)
              if (!isManualEntry) {
                setManualFestName(formik.values.festName)
              }
            }}
          >
            {isManualEntry ? 'Select from List' : 'Add Manually'}
          </button>
          {formik.errors.festName && (
            <div className='text-red-500'>{formik.errors.festName}</div>
          )}
        </div>

        <Field
          name='dateOfPost'
          label='Post Date'
          placeholder='Enter Post Date'
          type='date'
          component={CustomInput}
          onChange={(e) => formik.setFieldValue('dateOfPost', e.target.value)}
        />

        <button
          type='submit'
          disabled={isSubmitted}
          className='bg-primary text-xl font-medium text-white rounded-lg px-6 py-2'
        >
          {isSubmitted ? 'Submitting...' : 'Update'}
        </button>
      </form>
    </FormikProvider>
  )
}

export default Edit
